// ViewerToolbar.tsx — Shared viewport toolbar for all 3D viewers.
// Single source of truth for view-preset grid, zoom/pan buttons,
// shading modes, and toggle buttons.
//
// Optional props enable viewer-specific extras:
//   navMode / onToggleNavMode  — PanelViewer3D navigate/select pill
//   selectionCount / onDeleteSelection — PanelViewer3D delete button
//   onDownloadDebug            — PanelViewer3D debug log download

import { useShallowStore } from '../../store/useStore';
import type { ShadingMode, ViewPreset } from '../../types/panelvar';

interface ViewerToolbarProps {
  onCenterView: () => void;
  onZoomIn:     () => void;
  onZoomOut:    () => void;
  onPanLeft:    () => void;
  onPanRight:   () => void;
  onPanUp:      () => void;
  onPanDown:    () => void;
  // PanelViewer3D-specific extras (omit to hide)
  navMode?: 'navigate' | 'select';
  onToggleNavMode?: () => void;
  selectionCount?: number;
  onDeleteSelection?: () => void;
  onDownloadDebug?: () => void;
}

export default function ViewerToolbar({
  onCenterView,
  onZoomIn,
  onZoomOut,
  onPanLeft,
  onPanRight,
  onPanUp,
  onPanDown,
  navMode,
  onToggleNavMode,
  selectionCount = 0,
  onDeleteSelection,
  onDownloadDebug,
}: ViewerToolbarProps) {
  const {
    ui,
    setViewPreset,
    setShadingMode,
    toggleGrid,
    toggleGround,
    togglePanelMarks,
  } = useShallowStore(s => ({
    ui:               s.ui,
    setViewPreset:    s.setViewPreset,
    setShadingMode:   s.setShadingMode,
    toggleGrid:       s.toggleGrid,
    toggleGround:     s.toggleGround,
    togglePanelMarks: s.togglePanelMarks,
  }));

  const pb = (id: ViewPreset, label: string, title: string) => (
    <button
      key={id}
      className={`vt-btn${ui.viewPreset === id ? ' active' : ''}`}
      onClick={() => setViewPreset(id)}
      title={title}
    >
      {label}
    </button>
  );

  return (
    <div className="viewport-toolbar">
      {/* Navigate / Select mode pill — PanelViewer3D only */}
      {navMode !== undefined && onToggleNavMode && (
        <div className="nav-mode-pill" title="Toggle navigate / select mode (left-drag)">
          <button
            className={`nav-mode-btn${navMode === 'navigate' ? ' active' : ''}`}
            onClick={onToggleNavMode}
          >
            ⊙ Navigate
          </button>
          <button
            className={`nav-mode-btn${navMode === 'select' ? ' active' : ''}`}
            onClick={onToggleNavMode}
          >
            ⬚ Select
          </button>
        </div>
      )}

      {/* 3×3 view-preset grid */}
      <div className="vt-grid-3x3">
        {pb('bl-iso', '\u25CF', 'Back-Left Iso')}
        {pb('back',   'B',      'Back')}
        {pb('br-iso', '\u25CF', 'Back-Right Iso')}
        {pb('left',   'L',      'Left')}
        <button
          className={`vt-btn${ui.viewPreset === null ? ' active' : ''}`}
          onClick={onCenterView}
          title="Zoom Extents"
        >
          {'\u25A0'}
        </button>
        {pb('right',  'R',  'Right')}
        {pb('fl-iso', '\u25CF', 'Front-Left Iso')}
        {pb('front',  'F',  'Front')}
        {pb('fr-iso', '\u25CF', 'Front-Right Iso')}
      </div>

      {/* Top / Bottom */}
      <div className="vt-group">
        {pb('top',    'TP', 'Top')}
        {pb('bottom', 'BT', 'Bottom')}
      </div>

      {/* Zoom + pan arrows */}
      <div className="vt-group">
        <button className="vt-btn" onClick={onZoomIn}    title="Zoom In">+</button>
        <button className="vt-btn" onClick={onZoomOut}   title="Zoom Out">−</button>
        <span className="vt-sep">│</span>
        <button className="vt-btn" onClick={onPanLeft}   title="Pan Left">◀</button>
        <button className="vt-btn" onClick={onPanRight}  title="Pan Right">▶</button>
        <button className="vt-btn" onClick={onPanUp}     title="Pan Up">▲</button>
        <button className="vt-btn" onClick={onPanDown}   title="Pan Down">▼</button>
      </div>

      {/* Delete selected — PanelViewer3D only */}
      {selectionCount > 0 && onDeleteSelection && (
        <div className="vt-group">
          <button
            className="vt-btn vt-btn-danger"
            onClick={onDeleteSelection}
            title={`Delete ${selectionCount} selected entit${selectionCount > 1 ? 'ies' : 'y'}`}
          >
            🗑 {selectionCount}
          </button>
        </div>
      )}

      {/* Debug log download — PanelViewer3D only */}
      {onDownloadDebug && (
        <div className="vt-group">
          <button
            className="vt-btn"
            onClick={onDownloadDebug}
            title="Download camera debug log"
            style={{ fontSize: 10, color: '#999' }}
          >
            ⬇ Log
          </button>
        </div>
      )}

      {/* Shading modes */}
      <div className="vt-group vt-shading">
        {(['wireframe', 'hidden', 'shaded'] as ShadingMode[]).map(m => (
          <button
            key={m}
            className={`vt-btn${ui.shadingMode === m ? ' active' : ''}`}
            onClick={() => setShadingMode(m)}
          >
            {m.charAt(0).toUpperCase() + m.slice(1)}
          </button>
        ))}
      </div>

      {/* View toggles */}
      <div className="vt-group">
        <button className={`vt-btn${ui.showGrid        ? ' active' : ''}`} onClick={toggleGrid}        title="Toggle grid">Grid</button>
        <button className={`vt-btn${ui.showGround      ? ' active' : ''}`} onClick={toggleGround}      title="Toggle ground plane">Ground</button>
        <button className={`vt-btn${ui.showPanelMarks  ? ' active' : ''}`} onClick={togglePanelMarks}  title="Toggle panel marks">Marks</button>
      </div>
    </div>
  );
}
